import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  Card,
  Typography,
  List,
  ListItem,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import {
  useGetDepartmentQuery,
  useGetEmpScheduleDetailsByDateRangeQuery,
  useGetPositionQuery,
  useGetTeamQuery,
} from "../../../Api/attoDeskApi";
import {
  IDepartment,
  IPosition,
  ITeam,
} from "../../../Api/Interface/api.interface";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import useUser from "../../../Core/hook/useUser";
import TableViewIcon from "@mui/icons-material/TableView";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import jsPDF from "jspdf";
import "jspdf-autotable";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import * as XLSX from "xlsx";
import { CiSearch } from "react-icons/ci";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { RootState } from "../../../Store";

const formatTime = (time: string | undefined) => {
  if (!time) return "";
  const [hour, minute] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hour, minute);
  return new Intl.DateTimeFormat("en-CA", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  }).format(date);
};

type Schedule = {
  id: number;
  empid: number;
  day: string;
  startTime: string;
  endTime: string;
  isReSchedules: boolean;
  schedule_end_date: Dayjs;
  spansNextDay: boolean;
  status: string;
};

type EmployeeData = {
  empid: number;
  // teamDepartmentId: number;
  // teamPositionId: number;
  [date: string]: Schedule[] | number;
};

const capitalizeFirstLetter = (name: string) =>
  name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

const Index: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { userHasRight } = useUser();
  const isMobileRes = useIsMobile();
  const getStartOfWeek = () => dayjs().startOf("week");
  const getEndOfWeek = () => dayjs().endOf("week");
  const { companyLogo, logowidthPrint, companyName } = useSelector(
    (state: RootState) => state.company
  );
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(
    getStartOfWeek()
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs>(getEndOfWeek());
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [selectedIsDepartment, setSelectedProductIsDepartment] =
    useState<string>("Department");
  const [selectedIsPosition, setSelectedProductIsPosition] =
    useState<string>("Position");
  // const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | undefined>(
  //   undefined
  // );
  // const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | undefined>(
  //   undefined
  // );

  // const { data, isLoading, isError, refetch } =
  //   useGetEmpScheduleDetailsByDateRangeQuery(
  //     selectedStartDate && selectedEndDate
  //       ? {
  //           startDate: selectedStartDate.format("YYYY-MM-DD"),
  //           endDate: selectedEndDate.format("YYYY-MM-DD"),
  //           searchText: currentSearchQuery,
  //           positionId: selectedIsPosition,
  //           departmentId: selectedIsDepartment,
  //         }
  //       : {
  //           startDate: "",
  //           endDate: "",
  //           searchText: currentSearchQuery,
  //           positionId: selectedIsPosition,
  //           departmentId: selectedIsDepartment,
  //         }
  //   );

  const { data, isLoading, isError, refetch } =
    useGetEmpScheduleDetailsByDateRangeQuery(
      selectedStartDate && selectedEndDate
        ? {
            startDate: selectedStartDate.format("YYYY-MM-DD"),
            endDate: selectedEndDate.format("YYYY-MM-DD"),
            searchText: currentSearchQuery || "",
            positionId:
              selectedIsPosition && selectedIsPosition !== "Position"
                ? selectedIsPosition
                : "",
            departmentId:
              selectedIsDepartment && selectedIsDepartment !== "Department"
                ? selectedIsDepartment
                : "",
          }
        : {
            startDate: "",
            endDate: "",
            searchText: currentSearchQuery || "",
            positionId:
              selectedIsPosition && selectedIsPosition === "Position"
                ? selectedIsPosition
                : "",
            departmentId:
              selectedIsDepartment && selectedIsDepartment === "Department"
                ? selectedIsDepartment
                : "",
          }
    );

  const { data: tenantData, isLoading: tenantIsLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const { data: positionData, isLoading: positionLoading } =
    useGetPositionQuery({
      searchText: "",
    });

  const { data: departmentData, isLoading: departmentLoading } =
    useGetDepartmentQuery({
      searchText: "",
    });

  const teamList = useMemo(() => {
    return tenantData?.data as ITeam[];
  }, [tenantData?.data]);

  const positionList = useMemo(() => {
    return positionData?.data as IPosition[];
  }, [positionData?.data]);

  const postionMap = useMemo(() => {
    const map = new Map();
    positionList?.forEach((category) => {
      map.set(category.id, category.positionName);
    });
    return map;
  }, [positionList]);

  const departmentList = useMemo(() => {
    return departmentData?.data as IDepartment[];
  }, [departmentData?.data]);

  const teamMap = useMemo(() => {
    const map = new Map<
      number,
      {
        name: string;
        image: string;
        hourlySalary: string;
        otHourlySalary: string;
        positionName: string | null;
      }
    >();

    teamList?.forEach((teamMember) => {
      const fullName = `${capitalizeFirstLetter(
        teamMember.teamFirstName || ""
      )} ${capitalizeFirstLetter(teamMember.teamLastName || "")}`.trim();
      const positionName = postionMap.get(teamMember.teamPositionId) || null;

      map.set(teamMember.id, {
        name: fullName,
        image: teamMember.teamImg,
        hourlySalary: teamMember.teamHourlySalary,
        otHourlySalary: teamMember.teamOtHourlySalary,
        positionName,
      });
    });

    return map;
  }, [teamList, postionMap]);

  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setSelectedStartDate(newValue);
    }
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (newValue) {
      setSelectedEndDate(newValue);
    }
  };

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const goToPreviousDay = () => {
    const prevDay =
      selectedStartDate?.subtract(1, "day") || dayjs().subtract(1, "day");
    handleStartDateChange(prevDay);
  };

  const goToNextDay = () => {
    const nextDay = selectedStartDate?.add(1, "day") || dayjs().add(1, "day");
    handleStartDateChange(nextDay);
  };

  const goToPreviousDayEnd = () => {
    const prevDay =
      selectedEndDate?.subtract(1, "day") || dayjs().subtract(1, "day");
    handleEndDateChange(prevDay);
  };

  const goToNextDayEnd = () => {
    const nextDay = selectedEndDate?.add(1, "day") || dayjs().add(1, "day");
    handleEndDateChange(nextDay);
  };

  const employees: EmployeeData[] =
    data?.data && Array.isArray(data.data) ? data.data : [];

  const allDates = Array.from(
    new Set(
      employees.flatMap((emp) =>
        Object.keys(emp).filter((key) => key !== "empid")
      )
    )
  ).sort();

  const handleIsDepartment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValues = event.target.value;
    setSelectedProductIsDepartment(selectedValues);
  };

  const handleIsPosition = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValues = event.target.value;
    setSelectedProductIsPosition(selectedValues);
  };

  const handleExportToPDF = () => {
    const tableData = employees.map((emp) => {
      const scheduleData = allDates.map((date) => {
        const schedule = emp[date] as Schedule[];
        if (schedule && schedule.length > 0) {
          return schedule
            .map((s, index) => {
              return `${formatTime(s.startTime)} - ${formatTime(s.endTime)} ${
                s.spansNextDay ? "[next day]" : ""
              } ${s.isReSchedules ? "[R]" : ""}${
                s.schedule_end_date
                  ? s.status === "deleted"
                    ? " [D]"
                    : s.schedule_end_date && s.status === "updated"
                    ? " [Up]"
                    : ""
                  : ""
              }`;
            })
            .join(", ");
        } else {
          return "-";
        }
      });
      return [
        teamMap.get(emp.empid)?.name || "",
        teamMap.get(emp.empid)?.positionName || "",
        ...scheduleData,
      ];
    });

    // const tableHeaders = [
    //   [
    //     "Name",
    //     "Position",
    //     ...allDates.map(
    //       (date) =>
    //         `${date} (${new Intl.DateTimeFormat("en-CA", {
    //           weekday: "long",
    //         }).format(new Date(date))})`
    //     ),
    //   ],
    // ];

    const tableHeaders = [
      ["Name", "Position", ...allDates.map((date) => `${date}`)],
    ];

    const currentMonth = dayjs().format("YYYY MMM, DD");
    const startDateRange = dayjs(selectedStartDate).format("YYYY MMM, DD");
    const EndDateRange = dayjs(selectedEndDate).format("YYYY MMM, DD");

    const doc = new jsPDF("l", "mm", "a4");

    const imageUrl = companyLogo;
    const pageWidth = doc.internal.pageSize.width;
    const imgWidth = logowidthPrint;
    const imgHeight = 10;
    // const imgX = (pageWidth - imgWidth) / 2;
    const imgY = 10;
    const imgX = 15;

    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      doc.addImage(img, "PNG", imgX, imgY, imgWidth, imgHeight);

      const textStartY = imgY + imgHeight / 2;

      const centerText = (text: string, y: number) => {
        const textWidth = doc.getTextWidth(text);
        const textX = (pageWidth - textWidth) / 2;
        doc.text(text, textX, y);
      };

      const rightAlignText = (text: string, y: number) => {
        doc.setFontSize(10);
        const textWidth = doc.getTextWidth(text);
        const textX = pageWidth - textWidth - 15;
        doc.text(text, textX, y);
      };

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      rightAlignText(`Schedule Report`, textStartY);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      rightAlignText(
        `Period: ${startDateRange} to ${EndDateRange}`,
        textStartY + 5
      );

      (doc as any).autoTable({
        head: tableHeaders,
        body: tableData,
        startY: textStartY + 15,
        theme: "grid",
        styles: { fontSize: 8 },
        headStyles: { fillColor: [200, 200, 200] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });

      const fileName = `ScheduleReport_${startDateRange}_to_${EndDateRange}.pdf`;

      doc.save(fileName);
    };
  };

  const handleExportToAttendanceExcel = () => {
    const tableData = employees.map((emp) => {
      const scheduleData = allDates.map((date) => {
        const schedule = emp[date] as Schedule[];
        if (schedule && schedule.length > 0) {
          return schedule
            .map((s) => {
              return `${formatTime(s.startTime)} - ${formatTime(s.endTime)} ${
                s.spansNextDay ? "[next day]" : ""
              } ${s.isReSchedules ? "[R]" : ""}${
                s.schedule_end_date
                  ? s.status === "deleted"
                    ? " [D]"
                    : s.schedule_end_date && s.status === "updated"
                    ? " [Up]"
                    : ""
                  : ""
              }`;
            })
            .join(", ");
        } else {
          return "-";
        }
      });

      return [
        teamMap.get(emp.empid)?.name || "",
        teamMap.get(emp.empid)?.positionName || "",
        ...scheduleData,
      ];
    });

    // const tableHeaders = [
    //   [
    //     "Name",
    //     "Position",
    //     ...allDates.map(
    //       (date) =>
    //         `${dayjs(date).format("MMM-DD, YYYY")} (${new Intl.DateTimeFormat(
    //           "en-CA",
    //           {
    //             weekday: "long",
    //           }
    //         ).format(new Date(date))})`
    //     ),
    //   ],
    // ];

    const tableHeaders = [
      [
        "Name",
        "Position",
        ...allDates.map((date) => `${dayjs(date).format("MMM-DD, YYYY")}`),
      ],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([tableHeaders[0], ...tableData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ClockInOutList");

    const startDateRange = dayjs(selectedStartDate).format("YYYY MMM, DD");
    const endDateRange = dayjs(selectedEndDate).format("YYYY MMM, DD");
    const fileName = `ScheduleReport_${startDateRange}_to_${endDateRange}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  return (
    <Grid container spacing={3}>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Card className={classes.card}>
          <Grid
            container
            justifyContent={"space-between"}
            spacing={isMobileRes ? 2 : 3}
            sx={{ borderBottom: 2, padding: 2 }}
            className={classes.titleGrid}
          >
            <Grid item sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Grid
                container
                spacing={1}
                justifyContent="flex-start"
                sx={{ display: "flex" }}
              >
                <Grid item>
                  <Typography className={classes.cardTitle}>
                    Schedule Report
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Grid
                container
                spacing={1}
                justifyContent="flex-end"
                sx={{
                  display: "flex",
                  justifyContent: {
                    md: "flex-start",
                    lg: "flex-end",
                  },
                }}
              >
                <Grid item>
                  <TextField
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CiSearch className={classes.searchWidgetIcon23} />
                        </InputAdornment>
                      ),
                      classes: {
                        root: classes.formControlList,
                      },
                    }}
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Mobile & Name .."
                    size="small"
                    className={classes.searchTextField34}
                  />
                </Grid>
                <Grid item>
                  <List className={classes.ListPayroll}>
                    <ListItem className={classes.ListItemPayroll}>
                      <IconButton
                        color="primary"
                        onClick={goToPreviousDay}
                        aria-label="Previous Day"
                      >
                        <KeyboardArrowLeftIcon />
                      </IconButton>
                    </ListItem>
                    <ListItem className={classes.ListItemPayroll}>
                      <Button
                        className={classes.ListItemPayrollToday}
                        // onClick={goToToday}
                        // aria-label="Today"
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={selectedStartDate}
                            onChange={handleStartDateChange}
                            slotProps={{
                              textField: {
                                placeholder: "Start Date",
                                size: "small",
                                sx: {
                                  width: "140px",
                                },
                                InputProps: {
                                  // sx: {
                                  //   fontSize: 14,
                                  //   color: "#97c3df",
                                  // },
                                  // classes: {
                                  //   // root: classes.textFieldQuote,
                                  // },
                                  classes: {
                                    root: classes.formControlListStatus,
                                    notchedOutline: classes.notchedOutline,
                                  },
                                },
                                InputLabelProps: {
                                  sx: {
                                    fontSize: 14,
                                    color: "#97c3df",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Button>
                    </ListItem>
                    <ListItem className={classes.ListItemPayroll}>
                      <IconButton
                        color="primary"
                        onClick={goToNextDay}
                        aria-label="Previous Day"
                      >
                        <KeyboardArrowRightIcon />
                      </IconButton>
                    </ListItem>
                  </List>
                </Grid>
                <Grid item>
                  <List className={classes.ListPayroll}>
                    <ListItem className={classes.ListItemPayroll}>
                      <IconButton
                        color="primary"
                        onClick={goToPreviousDayEnd}
                        aria-label="Previous Day"
                      >
                        <KeyboardArrowLeftIcon />
                      </IconButton>
                    </ListItem>
                    <ListItem className={classes.ListItemPayroll}>
                      <Button
                        className={classes.ListItemPayrollToday}
                        // onClick={goToToday}
                        // aria-label="Today"
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={selectedEndDate}
                            onChange={handleEndDateChange}
                            slotProps={{
                              textField: {
                                placeholder: "End Date",
                                size: "small",
                                sx: {
                                  width: "140px",
                                },
                                InputProps: {
                                  sx: {
                                    fontSize: 14,
                                    color: "#97c3df",
                                  },
                                  // classes: {
                                  //   // root: classes.textFieldQuote,
                                  // },
                                  classes: {
                                    root: classes.formControlListStatus,
                                    notchedOutline: classes.notchedOutline,
                                  },
                                },
                                InputLabelProps: {
                                  sx: {
                                    fontSize: 14,
                                    color: "#97c3df",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Button>
                    </ListItem>
                    <ListItem className={classes.ListItemPayroll}>
                      <IconButton
                        color="primary"
                        onClick={goToNextDayEnd}
                        aria-label="Previous Day"
                      >
                        <KeyboardArrowRightIcon />
                      </IconButton>
                    </ListItem>
                  </List>
                </Grid>
                <Grid item>
                  <TextField
                    select
                    size="small"
                    sx={{ flexGrow: 1, width: "100%" }}
                    value={selectedIsDepartment || "Department"}
                    onChange={handleIsDepartment}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      classes: {
                        root: classes.formControlListStatus,
                      },
                    }}
                    className={classes.searchTextField34}
                  >
                    <MenuItem value={"Department"}>All Department</MenuItem>
                    {departmentList &&
                      departmentList.map((department) => (
                        <MenuItem key={department.id} value={department.id}>
                          {department.departmentName}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item>
                  <TextField
                    select
                    size="small"
                    sx={{ flexGrow: 1, width: "100%" }}
                    value={selectedIsPosition || ""}
                    onChange={handleIsPosition}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      classes: {
                        root: classes.formControlListStatus,
                      },
                    }}
                    className={classes.searchTextField34}
                  >
                    <MenuItem value={"Position"}>All Position</MenuItem>
                    {positionList &&
                      positionList.map((department) => (
                        <MenuItem key={department.id} value={department.id}>
                          {department.positionName}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item>
                  <Button
                    startIcon={<TableViewIcon />}
                    className={classes.addButtonPdfExcel}
                    onClick={handleExportToAttendanceExcel}
                  >
                    Excel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    startIcon={<LocalPrintshopSharpIcon />}
                    className={classes.addButtonPdfExcel}
                    onClick={handleExportToPDF}
                  >
                    pdf
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid>
            <TableContainer>
              <Table className={classes.table}>
                <TableHead className={classes.tableHeaderRow}>
                  <TableRow>
                    <TableCell className={classes.tablheader}>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell
                      className={classes.tablheader}
                      // sx={{ textAlign: "center" }}
                    >
                      <strong>Position</strong>
                    </TableCell>
                    {allDates.map((date) => (
                      <TableCell className={classes.tablheader} key={date}>
                        <Typography variant="body2" fontWeight="bold">
                          {date}
                        </Typography>
                        {/* <Typography variant="caption" textAlign={"center"}>
                          {new Intl.DateTimeFormat("en-US", {
                            weekday: "long",
                          }).format(new Date(date))}
                          {new Intl.DateTimeFormat("en-CA", {
                            weekday: "long",
                          }).format(new Date(date))}
                        </Typography> */}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading || positionLoading || tenantIsLoading ? (
                    <TableRow>
                      <TableCell colSpan={20} align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="10vh"
                        >
                          <CircularProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : isError || employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={20} align="center">
                        {isError
                          ? "Select Start and End Date"
                          : "No data available"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    employees.map((emp) => (
                      <TableRow
                        key={emp.empid}
                        className={classes.tableRowHover}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          className={classes.tablCell}
                        >
                          <Link
                            to={`/team-history/${emp.empid}`}
                            className={classes.iconLinkPropNormal}
                          >
                            <Typography className={classes.nameTableList}>
                              {teamMap.get(emp.empid)?.name || ""}
                              {/* <span
                                style={{
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  paddingLeft: "5px",
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faUser}
                                  style={{ color: "#1976d2" }}
                                />
                              </span> */}
                            </Typography>
                          </Link>
                        </TableCell>
                        <TableCell
                          scope="row"
                          className={classes.tablCell}
                          // sx={{ textAlign: "center" }}
                        >
                          {teamMap.get(emp.empid)?.positionName}
                        </TableCell>
                        {allDates.map((date) => {
                          const schedule = emp[date] as Schedule[];
                          return (
                            <TableCell
                              component="th"
                              scope="row"
                              className={classes.tablCell}
                              key={date}
                            >
                              {schedule && schedule.length > 0 ? (
                                schedule.map((s, index) => (
                                  <div key={index}>
                                    {formatTime(s.startTime)} -{" "}
                                    {`${formatTime(s.endTime)} ${
                                      s.spansNextDay ? "[next day]" : ""
                                    }`}{" "}
                                    {s.isReSchedules && (
                                      <span
                                        style={{
                                          color: "#e3791a", // orange for [R]
                                          fontWeight: 600,
                                          fontSize: "0.875rem",
                                        }}
                                      >
                                        [R]
                                      </span>
                                    )}
                                    {s.schedule_end_date && (
                                      <span
                                        style={{
                                          color: "#1976d2",
                                          fontWeight: 600,
                                          fontSize: "0.875rem",
                                          marginLeft: "4px",
                                        }}
                                      >
                                        [Up]
                                      </span>
                                    )}
                                    {s.schedule_end_date ? (
                                      s.status === "deleted" ? (
                                        <span
                                          style={{
                                            color: "#d32f2f", // red
                                            fontWeight: 600,
                                            paddingLeft: "1px",
                                          }}
                                        >
                                          [D]
                                        </span>
                                      ) : s.schedule_end_date &&
                                        s.status === "updated" ? (
                                        <span
                                          style={{
                                            color: "#1976d2", // blue
                                            fontWeight: 600,
                                            paddingLeft: "1px",
                                          }}
                                        >
                                          [Up]
                                        </span>
                                      ) : null
                                    ) : null}
                                    {index !== schedule.length - 1 && ", "}
                                  </div>
                                ))
                              ) : (
                                <Typography color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};

export default Index;
